/**
 * Evidence Portal — API client.
 *
 * All requests use Basic Auth stored in sessionStorage under a dedicated
 * `evidence_auth` key so the admin panel and the evidence portal do not
 * share state. On 401 the caller clears sessionStorage and shows login.
 */

import axios from 'axios';
import { API } from '../../../utils/api';

const AUTH_KEY = 'evidence_auth';
const BASE = `${API}/evidence`;

export const authHeader = () => {
  const token = sessionStorage.getItem(AUTH_KEY);
  return token ? { Authorization: `Basic ${token}` } : {};
};

export const setAuth = (username, password) => {
  const token = btoa(`${username}:${password}`);
  sessionStorage.setItem(AUTH_KEY, token);
};

export const clearAuth = () => sessionStorage.removeItem(AUTH_KEY);

export const isAuthed = () => Boolean(sessionStorage.getItem(AUTH_KEY));

const client = axios.create({ baseURL: BASE });

client.interceptors.request.use((config) => {
  Object.assign(config.headers, authHeader());
  return config;
});

// ---- Projects
export const listProjects = (q) =>
  client.get('/projects', { params: q ? { q } : {} }).then((r) => r.data);

export const createProject = (payload) =>
  client.post('/projects', payload).then((r) => r.data);

export const getProject = (id) => client.get(`/projects/${id}`).then((r) => r.data);

export const updateProject = (id, payload) =>
  client.patch(`/projects/${id}`, payload).then((r) => r.data);

export const deleteProject = (id) => client.delete(`/projects/${id}`);

// ---- Files
export const listFiles = (projectId, kind) =>
  client
    .get(`/projects/${projectId}/files`, { params: kind ? { kind } : {} })
    .then((r) => r.data);

export const uploadFiles = (projectId, files, onProgress) => {
  const fd = new FormData();
  Array.from(files).forEach((f) => fd.append('files', f));
  return client
    .post(`/projects/${projectId}/files`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Zip archives of 200+ survey photos can take a minute to upload + unpack.
      timeout: 5 * 60 * 1000,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
      },
    })
    .then((r) => r.data);
};

export const deleteFile = (fileId) => client.delete(`/files/${fileId}`);

// ---- AI cost ledger (Cost Meter widget)
export const getProjectAiCost = (projectId) =>
  client.get(`/projects/${projectId}/ai-cost`).then((r) => r.data);

// ---- Stage 3: outcome parsers
export const runParser = (projectId) =>
  client.post(`/projects/${projectId}/parse`).then((r) => r.data);

export const getOutcomes = (projectId, opts = {}) =>
  client
    .get(`/projects/${projectId}/outcomes`, {
      params: {
        ...(opts.category ? { category: opts.category } : {}),
        ...(opts.outcomeType ? { outcome_type: opts.outcomeType } : {}),
        evidence_relevant_only: opts.evidenceRelevantOnly ?? true,
      },
    })
    .then((r) => r.data);

export const getEvidenceQueue = (projectId) =>
  client.get(`/projects/${projectId}/evidence-queue`).then((r) => r.data);

export const getParserStatus = (projectId) =>
  client.get(`/projects/${projectId}/parser-status`).then((r) => r.data);

// ---- Stage 4: manual evidence links
export const attachEvidence = (outcomeId, fileId, note) =>
  client
    .post(`/outcomes/${outcomeId}/links`, null, {
      params: { file_id: fileId, ...(note ? { note } : {}) },
    })
    .then((r) => r.data);

export const detachEvidence = (outcomeId, linkId) =>
  client.delete(`/outcomes/${outcomeId}/links/${linkId}`);

export const confirmAiLink = (outcomeId, linkId) =>
  client.post(`/outcomes/${outcomeId}/links/${linkId}/confirm`).then((r) => r.data);

/**
 * Quick-add: create a research / technical_doc / site_note / assumption
 * record and (optionally) link it to an outcome in one call. Backend accepts
 * multipart form data — pass an already-built FormData for maximum flexibility.
 */
export const quickAddEvidence = (projectId, formData) =>
  client.post(`/projects/${projectId}/evidence`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 5 * 60 * 1000,
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
  }).then((r) => r.data);

/** One-shot EXIF backfill for older photos on a project. */
export const refreshExif = (projectId) =>
  client.post(`/projects/${projectId}/refresh-exif`).then((r) => r.data);

// Stage 4/5 endpoints
export const reprocessStoredZips = (projectId) =>
  client.post(`/projects/${projectId}/reprocess-zips`).then((r) => r.data);

/**
 * Stage 4 reconcile — asynchronous. Backend returns { job_id, total, ... }
 * immediately; poll `getReconcileStatus` until `status === 'done' | 'failed'`.
 * The synchronous path would exceed Cloudflare's ~100s edge timeout.
 */
export const reconcileProject = (projectId, opts = {}) =>
  client.post(`/projects/${projectId}/reconcile`, null, {
    params: opts,
    timeout: 30 * 1000,
  }).then((r) => r.data);

export const getReconcileStatus = (projectId, jobId) =>
  client.get(`/projects/${projectId}/reconcile/status`, {
    params: jobId ? { job_id: jobId } : {},
    timeout: 15 * 1000,
  }).then((r) => r.data);

/**
 * High-level helper: kicks off reconcile and polls status every 2s.
 * `onProgress({processed, total, matches_created, current_reference})`.
 * Resolves with the final job's result payload, rejects on failure.
 */
export const runReconcile = async (projectId, { onProgress, opts, pollMs = 2000, signal } = {}) => {
  const started = await reconcileProject(projectId, opts || {});
  const jobId = started.job_id;
  if (onProgress) onProgress({ ...started, processed: 0, matches_created: 0 });
  // Poll loop
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (signal?.aborted) throw new Error('Reconcile cancelled');
    await new Promise((r) => setTimeout(r, pollMs));
    let s;
    try {
      s = await getReconcileStatus(projectId, jobId);
    } catch (e) {
      // transient — keep polling; the job is server-side
      continue;
    }
    if (onProgress) onProgress(s);
    if (s.status === 'done') return s;
    if (s.status === 'failed') {
      const err = new Error(s.error || 'Reconcile failed');
      err.job = s;
      throw err;
    }
  }
};
export const refreshRedFlags = (projectId) =>
  client.post(`/projects/${projectId}/red-flags/refresh`).then((r) => r.data);
export const listRedFlags = (projectId) =>
  client.get(`/projects/${projectId}/red-flags`).then((r) => r.data);
export const resolveRedFlag = (projectId, flagId, resolution, rationale) => {
  const fd = new FormData();
  fd.append('resolution', resolution);
  fd.append('rationale', rationale || '');
  return client.post(`/projects/${projectId}/red-flags/${flagId}/resolve`, fd).then((r) => r.data);
};
export const getReportPreview = (projectId) =>
  client.get(`/projects/${projectId}/report-preview`).then((r) => r.data);
export const downloadReportDocx = (projectId) =>
  client.get(`/projects/${projectId}/report.docx`, { responseType: 'blob', timeout: 300000 }).then((r) => r.data);
export const startReportJob = (projectId) =>
  client.post(`/projects/${projectId}/report-jobs`).then((r) => r.data);
export const getReportJob = (projectId, jobId) =>
  client.get(`/projects/${projectId}/report-jobs/${jobId}`).then((r) => r.data);
export const downloadLatestReportDocx = (projectId) =>
  client.get(`/projects/${projectId}/reports/latest.docx`, { responseType: 'blob' }).then((r) => r.data);
export const goldStandardCompare = (projectId, file) => {
  const fd = new FormData();
  fd.append('reference', file);
  return client.post(`/projects/${projectId}/gold-standard-compare`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data);
};

export const updateFileKind = (fileId, kind) =>
  client.patch(`/files/${fileId}`, null, { params: { kind } }).then((r) => r.data);

/** Fetch the thumbnail (or original) as a blob URL for use in <img src>. */
export const fetchFileAsBlobUrl = async (fileId, kind = 'thumbnail') => {
  const path = kind === 'thumbnail' ? `/files/${fileId}/thumbnail` : `/files/${fileId}`;
  const resp = await client.get(path, { responseType: 'blob' });
  return URL.createObjectURL(resp.data);
};

/** Verify current session is valid — used post-login. */
export const ping = async () => {
  await client.get('/projects', { params: { q: '__ping__' } });
  return true;
};
